import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home"; // eager — LCP route
import { AnalyticsRouteTracker } from "@/lib/analytics";
import ScrollReveal from "@/components/site/ScrollReveal";

// BuddyChat is heavy (chat UI + lucide icons + local storage plumbing) and
// nobody needs it during LCP. Defer it until the browser is idle or ~2s pass.
const BuddyChat = lazy(() => import("@/components/site/BuddyChat"));

// Code-split every non-home route so the initial bundle shrinks dramatically.
// Each route becomes its own async chunk fetched only when the user navigates
// there.
const Products = lazy(() => import("@/pages/Products"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Quote = lazy(() => import("@/pages/Quote"));
const Book = lazy(() => import("@/pages/Book"));
const Contact = lazy(() => import("@/pages/Contact"));
const Admin = lazy(() => import("@/pages/Admin"));
const Buy = lazy(() => import("@/pages/Buy"));
const BuySuccess = lazy(() => import("@/pages/BuySuccess"));
const Repair = lazy(() => import("@/pages/Repair"));
const Terms = lazy(() => import("@/pages/Terms"));
const Policy = lazy(() => import("@/pages/Policy"));
const Review = lazy(() => import("@/pages/Review"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const BlogIndex = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogIndex })));
const BlogPost = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogPost })));
const Location = lazy(() => import("@/pages/Location"));

// Wrapper that only mounts BuddyChat after the browser is idle or ~2s pass —
// keeps the chat launcher available quickly without competing with LCP.
function DeferredBuddyChat() {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const w = typeof window !== "undefined" ? window : null;
        if (!w) return;
        const t = setTimeout(() => setReady(true), 2000);
        const idleId = "requestIdleCallback" in w
            ? w.requestIdleCallback(() => setReady(true), { timeout: 2000 })
            : null;
        return () => {
            clearTimeout(t);
            if (idleId !== null && "cancelIdleCallback" in w) w.cancelIdleCallback(idleId);
        };
    }, []);
    if (!ready) return null;
    return (
        <Suspense fallback={null}>
            <BuddyChat />
        </Suspense>
    );
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function Layout({ children }) {
    const { pathname } = useLocation();
    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Navbar />
            {/* key={pathname} forces a fresh mount on every route change so the fade-in replays */}
            <main key={pathname} className="flex-1 fl-page-fade-in">
                {children}
            </main>
            <Footer />
        </div>
    );
}

// Minimal, brand-consistent fallback shown briefly while a lazy route loads.
function RouteFallback() {
    return (
        <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true">
            <div className="text-flow-muted text-xs tracking-[0.25em] uppercase">Loading…</div>
        </div>
    );
}

function App() {
    return (
        <div className="App font-light">
            <BrowserRouter>
                <ScrollToTop />
                <ScrollReveal />
                <AnalyticsRouteTracker />
                <DeferredBuddyChat />
                <Toaster position="top-center" richColors closeButton />
                <Suspense fallback={<Layout><RouteFallback /></Layout>}>
                    <Routes>
                        <Route path="/" element={<Layout><Home /></Layout>} />
                        <Route path="/products" element={<Layout><Products /></Layout>} />
                        <Route path="/products/:id" element={<Layout><ProductDetail /></Layout>} />
                        <Route path="/quote" element={<Layout><Quote /></Layout>} />
                        <Route path="/book" element={<Layout><Book /></Layout>} />
                        <Route path="/contact" element={<Layout><Contact /></Layout>} />
                        <Route path="/admin" element={<Layout><Admin /></Layout>} />
                        <Route path="/admin6767" element={<Layout><Admin /></Layout>} />
                        <Route path="/buy/:id" element={<Layout><Buy /></Layout>} />
                        <Route path="/buy/success" element={<Layout><BuySuccess /></Layout>} />
                        <Route path="/installation" element={<Layout><Book /></Layout>} />
                        <Route path="/repair" element={<Layout><Repair /></Layout>} />
                        <Route path="/terms" element={<Layout><Terms /></Layout>} />
                        <Route path="/policy" element={<Layout><Policy /></Layout>} />
                        <Route path="/review" element={<Layout><Review /></Layout>} />
                        <Route path="/review/:id" element={<Layout><Review /></Layout>} />
                        <Route path="/reviews" element={<Layout><Reviews /></Layout>} />
                        <Route path="/faq" element={<Layout><FAQ /></Layout>} />
                        <Route path="/blog" element={<Layout><BlogIndex /></Layout>} />
                        <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
                        <Route path="/locations/:slug" element={<Layout><Location /></Layout>} />
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </div>
    );
}

export default App;
